<template>
  <div class="pe">
    <div class="pe-bar">
      <button class="pe-btn" @click="addPlacement">Add widget</button>
      <button class="pe-btn pe-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
      <button class="pe-btn" @click="$emit('cancel')">Cancel</button>
    </div>
    <div v-if="saveError" class="pe-error">{{ saveError }}</div>

    <div class="pe-stage">
      <div class="pe-stagebg" :style="stageStyle(draft)">
        <div class="pe-list">
          <button
            v-for="p in draft.placements"
            :key="p.id"
            type="button"
            class="pe-chip"
            :class="{ 'pe-chip-on': selectedId === p.id }"
            @click="selectedId = selectedId === p.id ? null : p.id"
          >{{ p.id }}</button>
        </div>
        <PageTemplate
          v-if="templateOk"
          :key="previewKey"
          :template="draft.template"
          :placements="draft.placements"
          :widgets="previewWidgets"
          :host-props="{ pageId, filters: values, spec: draft }"
        />
        <div v-else class="pe-error">Every placement needs one &lt;widget id="..."&gt; in the template before the preview can draw.</div>
      </div>

      <aside class="pe-panel">
        <template v-if="selected">
          <div class="pe-panel-title">Placement</div>
          <label class="pe-field"><span>Widget</span>
            <select v-model="selected.widget" @change="onWidgetChange(selected)">
              <option v-for="w in widgetList" :key="w.slug" :value="w.slug">{{ w.name }}{{ w.stock ? '' : ' (custom)' }}</option>
            </select>
          </label>
          <div v-if="contractParams(selected).length" class="pe-hint">Reads the page variables: {{ contractParams(selected).join(', ') }}</div>
          <div v-for="inp in contractInputs(selected)" :key="inp.name" class="pe-input">
            <div class="pe-input-name">Input <b>{{ inp.name }}</b> <span v-if="inp.description">— {{ inp.description }}</span></div>
            <select :value="bindingKey(selected, inp.name)" @change="bind(selected, inp.name, $event.target.value)">
              <option value="">Choose a source…</option>
              <optgroup v-if="pageSourceNames.length" label="Page sources">
                <option v-for="n in pageSourceNames" :key="n" :value="'source:' + n">{{ n }}</option>
              </optgroup>
              <optgroup label="Governed objects">
                <option v-for="s in sourcesFor(inp)" :key="s.kind + ':' + s.id" :value="s.kind + ':' + s.id">{{ s.kind }} · {{ s.name }}</option>
              </optgroup>
            </select>
            <label v-if="ownBinding(selected, inp.name)" class="pe-field"><span>Follow variable instead of that id</span>
              <input :value="variableOf(selected, inp.name)" type="text" placeholder="metric" @change="followVariable(selected, inp.name, $event.target.value)">
            </label>
            <div v-if="inp.columns && inp.columns.length" class="pe-hint">needs {{ inp.columns.map((c) => c.name).join(', ') }}</div>
          </div>
          <label class="pe-field"><span>Config (JSON)</span>
            <textarea v-model="configText" rows="8" @blur="applyConfig" />
          </label>
          <div v-if="configError" class="pe-error">{{ configError }}</div>
          <label class="pe-field"><span>Implicit values (JSON)</span>
            <textarea v-model="implicitText" rows="3" placeholder="{&quot;metric&quot;: 51}" @blur="applyPlacementJson('implicit', implicitText, {})" />
          </label>
          <label class="pe-field"><span>Widget filters (JSON)</span>
            <textarea v-model="widgetFiltersText" rows="4" @blur="applyPlacementJson('filters', widgetFiltersText, [])" />
          </label>
          <div v-if="placementJsonError" class="pe-error">{{ placementJsonError }}</div>
          <label class="pe-field"><span>Intent</span><input v-model="selected.intent" type="text"></label>
          <div class="pe-panel-sub">Style</div>
          <label class="pe-field"><span>Background</span>
            <div class="pe-color">
              <input :value="look.background || '#ffffff'" type="color" @input="setLook('background', $event.target.value)">
              <input :value="look.background || ''" type="text" placeholder="#ffffff" @change="setLook('background', $event.target.value)">
            </div>
          </label>
          <label class="pe-field"><span>Border colour</span>
            <div class="pe-color">
              <input :value="look.border_color || '#e6e4df'" type="color" @input="setLook('border_color', $event.target.value)">
              <input :value="look.border_color || ''" type="text" placeholder="#e6e4df" @change="setLook('border_color', $event.target.value)">
            </div>
          </label>
          <div class="pe-row">
            <label class="pe-field pe-num"><span>Border</span><input :value="look.border_width ?? ''" type="number" min="0" max="12" placeholder="1" @change="setLook('border_width', $event.target.value)"></label>
            <label class="pe-field pe-num"><span>Radius</span><input :value="look.border_radius ?? ''" type="number" min="0" max="48" placeholder="8" @change="setLook('border_radius', $event.target.value)"></label>
            <label class="pe-field pe-num"><span>Padding</span><input :value="look.padding ?? ''" type="number" min="0" max="64" placeholder="0" @change="setLook('padding', $event.target.value)"></label>
          </div>
          <label class="pe-field"><span>Shadow</span>
            <select :value="look.shadow || ''" @change="setLook('shadow', $event.target.value)">
              <option value="">Default (none)</option>
              <option value="sm">Small</option>
              <option value="md">Medium</option>
            </select>
          </label>
          <button class="pe-btn pe-danger" @click="removePlacement(selected.id)">Remove</button>
        </template>
        <template v-else>
          <div class="pe-panel-title">Page</div>
          <label class="pe-field"><span>Layout (HTML with Tailwind classes; one &lt;widget id="..."&gt; per placement)</span>
            <textarea v-model="draft.template" rows="14" class="pe-code" spellcheck="false" />
          </label>
          <label class="pe-field"><span>Background</span>
            <div class="pe-color">
              <input :value="draft.canvas.background || '#ffffff'" type="color" @input="draft.canvas.background = $event.target.value">
              <input v-model="draft.canvas.background" type="text" placeholder="transparent">
            </div>
          </label>
          <label class="pe-field"><span>Padding (px)</span><input v-model.number="draft.canvas.padding" type="number" min="0" max="200"></label>
          <div v-if="!subject" class="pe-field"><span>Folder</span><FolderPicker v-model="draftStem" :stems="stems" /></div>
          <label class="pe-field pe-check"><input v-model="draftShared" type="checkbox"><span>Shared with the organisation</span></label>
          <div v-if="subject" class="pe-hint">This is the page for {{ subject }}{{ subjectId ? ' ' + subjectId : '' }}.</div>
          <label class="pe-field"><span>Filters the viewer sees (JSON)</span>
            <textarea v-model="filtersText" rows="6" @blur="applyFilters" />
          </label>
          <div v-if="filtersError" class="pe-error">{{ filtersError }}</div>
          <label class="pe-field"><span>Implicit values (JSON): names the host supplies, null to leave to it</span>
            <textarea v-model="implicitPageText" rows="4" @blur="applyPageJson('implicit', implicitPageText, {})" />
          </label>
          <label class="pe-field"><span>Data sources (JSON): name to {kind, id, params}</span>
            <textarea v-model="sourcesText" rows="8" @blur="applyPageJson('sources', sourcesText, {})" />
          </label>
          <div v-if="pageJsonError" class="pe-error">{{ pageJsonError }}</div>
          <div class="pe-hint">Select a placement above the preview to edit it.</div>
        </template>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import FolderPicker from '@/components/Pages/FolderPicker.vue';
import { allStems, domainStems } from '@/utils/pageFolders';
import { filterDefaults, stageStyle } from './pageLayout';
import PageTemplate from './PageTemplate.vue';

function clone(v) {
  return JSON.parse(JSON.stringify(v));
}

function addSlot(template, id) {
  const slot = `<widget id="${id}"></widget>`;
  const html = (template || '').trim();
  if (!html) return `<div class="flex flex-col gap-5">\n  ${slot}\n</div>`;
  const at = html.lastIndexOf('</div>');
  return at === -1 ? `${html}\n${slot}` : `${html.slice(0, at)}  ${slot}\n${html.slice(at)}`;
}

function removeSlot(template, id) {
  return (template || '').replace(new RegExp(`\\s*<widget[^>]*id="${id}"[^>]*>\\s*</widget>`), '');
}

export default {
  name: 'PageEditor',
  components: { PageTemplate, FolderPicker },
  props: {
    pageId: { type: Number, required: true },
    spec: { type: Object, required: true },
    widgets: { type: Object, default: () => ({}) },
    subject: { type: String, default: '' },
    subjectId: { type: Number, default: null },
    shared: { type: Boolean, default: false },
    stem: { type: String, default: '' },
  },
  emits: ['saved', 'cancel'],
  data() {
    const draft = clone(this.spec);
    draft.canvas = draft.canvas || {};
    draft.template = draft.template || '';
    return {
      draft,
      draftShared: this.shared,
      draftStem: this.stem,
      stems: [],
      implicitText: '',
      widgetFiltersText: '',
      placementJsonError: '',
      implicitPageText: JSON.stringify(draft.implicit || {}, null, 2),
      sourcesText: JSON.stringify(draft.sources || {}, null, 2),
      pageJsonError: '',
      values: filterDefaults(draft),
      selectedId: null,
      saving: false,
      saveError: '',
      widgetList: [],
      sources: [],
      configText: '',
      configError: '',
      filtersText: JSON.stringify(draft.filters || [], null, 2),
      filtersError: '',
    };
  },
  computed: {
    templateOk() {
      const html = this.draft.template || '';
      return (this.draft.placements || []).every((p) => html.includes(`id="${p.id}"`));
    },
    previewKey() {
      return JSON.stringify([this.draft.template, (this.draft.placements || []).map((p) => [p.id, p.widget])]);
    },
    previewWidgets() {
      return Object.fromEntries((this.draft.placements || []).map((p) => [p.widget, this.widgetDef(p.widget)]).filter(([, w]) => w));
    },
    selected() {
      return (this.draft.placements || []).find((p) => p.id === this.selectedId) || null;
    },
    look() {
      return (this.selected && this.selected.style) || {};
    },
    pageSourceNames() {
      return Object.keys(this.draft.sources || {});
    },
  },
  watch: {
    selectedId() {
      this.configText = this.selected ? JSON.stringify(this.selected.config || {}, null, 2) : '';
      this.implicitText = this.selected ? JSON.stringify(this.selected.implicit || {}, null, 2) : '';
      this.widgetFiltersText = this.selected ? JSON.stringify(this.selected.filters || [], null, 2) : '';
      this.configError = '';
      this.placementJsonError = '';
    },
  },
  async mounted() {
    const [{ data: w }, { data: s }, { data: pages }, domains] = await Promise.all([
      axios.get('/api/impact_analytics/widgets/'),
      axios.get('/api/impact_analytics/agent-pages/sources/'),
      axios.get('/api/impact_analytics/agent-pages/'),
      domainStems(),
    ]);
    this.widgetList = w.widgets || [];
    this.sources = s.sources || [];
    this.stems = [...new Set([...domains, ...allStems(pages.filter((page) => page.mine && !page.subject_type))])];
  },
  methods: {
    stageStyle,
    widgetDef(slug) {
      return this.widgetList.find((w) => w.slug === slug) || this.widgets[slug] || null;
    },
    contractInputs(p) {
      const w = this.widgetDef(p.widget);
      return ((w && w.contract) || {}).inputs || [];
    },
    sourcesFor(inp) {
      const kinds = inp.kinds && inp.kinds.length ? inp.kinds : null;
      return this.sources.filter((s) => !kinds || kinds.includes(s.kind));
    },
    contractParams(p) {
      const w = this.widgetDef(p.widget);
      return (((w && w.contract) || {}).params || []).map((x) => x.name);
    },
    bindingKey(p, name) {
      const b = (p.inputs || {})[name];
      if (!b) return '';
      return b.source ? `source:${b.source}` : `${b.kind}:${typeof b.id === 'object' ? '' : b.id}`;
    },
    ownBinding(p, name) {
      const b = (p.inputs || {})[name];
      return !!b && !b.source;
    },
    variableOf(p, name) {
      const b = (p.inputs || {})[name];
      return b && b.id && typeof b.id === 'object' ? b.id.var : '';
    },
    followVariable(p, name, variable) {
      const b = p.inputs[name];
      const text = String(variable || '').trim();
      if (text) b.id = { var: text };
      else delete p.inputs[name];
    },
    bind(p, name, key) {
      if (!p.inputs) p.inputs = {};
      if (!key) {
        delete p.inputs[name];
        return;
      }
      if (key.startsWith('source:')) {
        p.inputs[name] = { source: key.slice(7) };
        return;
      }
      const [kind, id] = key.split(':');
      const source = this.sources.find((s) => s.kind === kind && String(s.id) === id);
      p.inputs[name] = { kind, id: Number(id), params: {}, label: source ? source.name : '' };
    },
    onWidgetChange(p) {
      const names = new Set(this.contractInputs(p).map((i) => i.name));
      Object.keys(p.inputs || {}).forEach((n) => { if (!names.has(n)) delete p.inputs[n]; });
    },
    applyConfig() {
      if (!this.selected) return;
      try {
        const parsed = JSON.parse(this.configText || '{}');
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Config must be a JSON object.');
        this.selected.config = parsed;
        this.configError = '';
      } catch (e) {
        this.configError = e.message;
      }
    },
    applyPlacementJson(field, text, empty) {
      if (!this.selected) return;
      try {
        const parsed = JSON.parse(text || JSON.stringify(empty));
        if (Array.isArray(parsed) !== Array.isArray(empty) || typeof parsed !== 'object' || parsed === null) {
          throw new Error(Array.isArray(empty) ? `${field} must be a JSON list.` : `${field} must be a JSON object.`);
        }
        if (Array.isArray(parsed) ? parsed.length : Object.keys(parsed).length) this.selected[field] = parsed;
        else delete this.selected[field];
        this.placementJsonError = '';
      } catch (e) {
        this.placementJsonError = e.message;
      }
    },
    applyPageJson(field, text, empty) {
      try {
        const parsed = JSON.parse(text || JSON.stringify(empty));
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) throw new Error(`${field} must be a JSON object.`);
        if (Object.keys(parsed).length) this.draft[field] = parsed;
        else delete this.draft[field];
        this.pageJsonError = '';
      } catch (e) {
        this.pageJsonError = e.message;
      }
    },
    applyFilters() {
      try {
        const parsed = JSON.parse(this.filtersText || '[]');
        if (!Array.isArray(parsed)) throw new Error('Filters must be a JSON list.');
        this.draft.filters = parsed;
        this.filtersError = '';
      } catch (e) {
        this.filtersError = e.message;
      }
    },
    addPlacement() {
      const base = 'placement';
      let n = (this.draft.placements || []).length + 1;
      while (this.draft.placements.some((p) => p.id === `${base}-${n}`)) n += 1;
      const first = this.widgetList.find((w) => w.slug === 'heading') || this.widgetList[0];
      const placement = {
        id: `${base}-${n}`, widget: first ? first.slug : 'heading', inputs: {},
        config: first && first.slug === 'heading' ? { text: 'New section', level: 2 } : {},
        intent: '',
      };
      this.draft.placements.push(placement);
      this.draft.template = addSlot(this.draft.template, placement.id);
      this.selectedId = placement.id;
    },
    setLook(key, raw) {
      const p = this.selected;
      if (!p) return;
      const style = { ...(p.style || {}) };
      const numeric = ['border_width', 'border_radius', 'padding'].includes(key);
      const value = numeric ? (raw === '' ? null : Number(raw)) : String(raw || '').trim();
      if (value === null || value === '') delete style[key];
      else style[key] = value;
      if (Object.keys(style).length) p.style = style;
      else delete p.style;
    },
    removePlacement(id) {
      this.draft.placements = this.draft.placements.filter((p) => p.id !== id);
      this.draft.template = removeSlot(this.draft.template, id);
      this.selectedId = null;
    },
    async save() {
      this.applyConfig();
      this.applyFilters();
      this.applyPageJson('implicit', this.implicitPageText, {});
      this.applyPageJson('sources', this.sourcesText, {});
      if (this.selected) {
        this.applyPlacementJson('implicit', this.implicitText, {});
        this.applyPlacementJson('filters', this.widgetFiltersText, []);
      }
      if (this.configError || this.filtersError || this.pageJsonError || this.placementJsonError) return;
      this.saving = true;
      this.saveError = '';
      try {
        const body = { spec: this.draft, shared: this.draftShared };
        if (!this.subject) body.stem = this.draftStem;
        const { data } = await axios.put(`/api/impact_analytics/agent-pages/${this.pageId}/spec/`, body);
        this.$emit('saved', data);
      } catch (err) {
        this.saveError = err?.response?.data?.error || 'The page could not be saved.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.pe { display: flex; flex-direction: column; gap: 10px; }
.pe-bar { display: flex; justify-content: flex-end; gap: 6px; }
.pe-btn { font-size: 12.5px; padding: 5px 10px; border: 1px solid #dad8d2; border-radius: 6px; background: #fff; color: #1a1918; cursor: pointer; }
.pe-primary { background: #EF6C3B; border-color: #EF6C3B; color: #fff; }
.pe-danger { color: #C0392B; margin-top: 8px; }
.pe-error { font-size: 12px; color: #c23a28; }
.pe-stage { display: flex; gap: 12px; align-items: flex-start; }
.pe-stagebg { flex: 1; min-width: 0; }
.pe-list { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.pe-chip { font-size: 11.5px; padding: 3px 8px; border: 1px solid #dad8d2; border-radius: 999px; background: #fff; color: #56534d; }
.pe-chip-on { border-color: #EF6C3B; color: #9F4D30; background: #FFF7F4; }
.pe-code { font-family: 'IBM Plex Mono', monospace; font-size: 11.5px; }
.pe-panel { width: 300px; flex: none; background: #fff; border: 1px solid #e6e4df; border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 8px; font-size: 12.5px; }
.pe-panel-title { font-weight: 600; color: #1a1918; }
.pe-panel-sub { font-weight: 600; color: #56534d; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.03em; margin-top: 6px; padding-top: 8px; border-top: 1px solid #f0efec; }
.pe-field { display: flex; flex-direction: column; gap: 3px; color: #56534d; }
.pe-field input, .pe-field select, .pe-field textarea, .pe-input select { font-size: 12.5px; padding: 5px 6px; border: 1px solid #dad8d2; border-radius: 6px; background: #fff; font-family: inherit; }
.pe-field textarea { font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 11.5px; }
.pe-row { display: flex; gap: 6px; }
.pe-color { display: flex; gap: 6px; align-items: center; }
.pe-color input[type="color"] { width: 32px; height: 28px; padding: 2px; }
.pe-color input[type="text"] { flex: 1; min-width: 0; }
.pe-num { flex: 1; }
.pe-input { display: flex; flex-direction: column; gap: 3px; }
.pe-input-name { color: #56534d; }
.pe-hint { font-size: 11px; color: #8b877f; }
.pe-check { flex-direction: row; align-items: center; gap: 6px; }
</style>
